import { useOktaAuth } from "@okta/okta-react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { GetCustomError } from "shared/helpers/AxiosHelpers";

import { UPLOAD_HISTORY_835_URL } from "../constants/ImportConversion835Constants";

const dataKey = "useFetchImportConversion835UploadHistoryKey";

/**
 * Recent 835 upload history for one organization.
 *
 * The organization id and type are both part of the query key. They have to travel together:
 * the same id means an RPID for a parent company and a group id for a group, so keying on the
 * id alone would serve one organization's history to the other after a header switch.
 *
 * The query stays disabled until an organization is selected. The server rejects a request
 * without one, so firing it would only produce a guaranteed 400.
 *
 * @param {{ value: number|string, type: string }} currentOrganization
 *   The header selection, as held on UserContext.
 */
export function useFetchImportConversion835UploadHistory(currentOrganization) {
  const { oktaAuth } = useOktaAuth();

  const organizationId = currentOrganization ? currentOrganization.value : null;
  const organizationType = currentOrganization ? currentOrganization.type : null;

  const hasOrganization = Boolean(organizationId && organizationType);

  return useQuery({
    queryKey: [dataKey, organizationId, organizationType],
    queryFn: async ({ signal }) => {
      const accessToken = oktaAuth.getAccessToken();

      try {
        const response = await axios.get(UPLOAD_HISTORY_835_URL, {
          params: { organizationId, organizationType },
          headers: { Authorization: `Bearer ${accessToken}` },
          signal,
        });

        return response.data;
      } catch (error) {
        throw GetCustomError(error);
      }
    },
    enabled: hasOrganization,
    refetchOnWindowFocus: false,
  });
}
